import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import './phases.css';

// Archivio persistente delle operazioni AI in background: sopravvive al refresh (localStorage),
// riprende da solo il polling dei job ancora in corso e mostra l'esito. Le finestre riportano
// il proprio stato con useBgReport; i risultati "job" (con jobId) sono ripristinabili dal server.
const LS_KEY = 'ai-bg-jobs';

// ── Icone (registry per chiave: niente JSX in localStorage) ──
const I = { size: 34 };
const ICONS = {
  '456': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 3h3l1.5 5-2 1.5a12 12 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3z" /></svg>,
  indaga: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7.6" /><path d="M21.5 21.5 15.4 15.4" /><path d="M10 5.9c-1.85 0-3.25 1.35-3.25 3.1 0 2.1 3.25 5.1 3.25 5.1s3.25-3 3.25-5.1C13.25 7.25 11.85 5.9 10 5.9z" /><circle cx="10" cy="9.1" r="1.15" fill="currentColor" stroke="none" /></svg>,
  dominio: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>,
  lucchetto: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>,
  copsm: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5a9 9 0 0 1 14 0M8 15.5a5 5 0 0 1 8 0" /><circle cx="12" cy="19" r="1.3" fill="currentColor" stroke="none" /></svg>,
  coptim: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5a9 9 0 0 1 14 0M8 15.5a5 5 0 0 1 8 0" /><circle cx="12" cy="19" r="1.3" fill="currentColor" stroke="none" /></svg>,
};
const AiGlyph = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5a2.5 2.5 0 0 0-4.9-.5A2.6 2.6 0 0 0 4 7a2.6 2.6 0 0 0-.4 3.1 2.7 2.7 0 0 0 .4 4.3 2.6 2.6 0 0 0 2.5 2.8A2.5 2.5 0 0 0 12 18.7z" />
    <path d="M12 5a2.5 2.5 0 0 1 4.9-.5A2.6 2.6 0 0 1 20 7a2.6 2.6 0 0 1 .4 3.1 2.7 2.7 0 0 1-.4 4.3 2.6 2.6 0 0 1-2.5 2.8A2.5 2.5 0 0 1 12 18.7z" />
    <path d="M12 5v13.7" />
  </svg>
);

// ── Resumers: come ripescare l'esito di un job (jobId valido lato server) dopo il refresh ──
const n456Esito = (r) => ({ TIM: 'TIM', 'NON TIM': 'NON TIM', occupato: 'Occupato', no_risposta: 'Nessuna risposta', numero_inesistente: 'Numero inesistente', errore: 'Errore' }[r] || r || 'esito');
const TERMIN = ['completato', 'errore', 'annullato'];
const RESUMERS = {
  '456': async (id) => { const d = (await axios.get(`${API}/ricerca-online/${id}`)).data; if (!(d.stato === 'completato' || d.stato === 'errore')) return null; return { error: d.stato === 'errore', summary: n456Esito(d.risultato) }; },
  dominio: async (id) => { const d = (await axios.get(`${API}/dominio/verifica/${id}`)).data; if (!(d.stato === 'completato' || d.stato === 'errore')) return null; return { error: d.stato === 'errore', summary: d.stato === 'errore' ? 'errore' : 'completato' }; },
  lucchetto: async (id) => { const d = (await axios.get(`${API}/staff-ai/lucchetto/${id}`)).data; if (!TERMIN.includes(d.stato)) return null; const ri = d.risultato; return { error: d.stato === 'errore' || (ri && !ri.success), summary: ri && ri.success ? (ri.lucchetto || 'esito') : (d.errore || 'non riuscito') }; },
  copsm: async (id) => { const d = (await axios.get(`${API}/staff-ai/copertura-sm/${id}`)).data; if (!TERMIN.includes(d.stato)) return null; const ri = d.risultato; return { error: d.stato === 'errore', summary: ri && ri.trovato ? 'indirizzo trovato' : 'non trovato' }; },
};

const BgCtx = createContext(null);
export const useBg = () => useContext(BgCtx);
export const useBgJob = (key) => { const bg = useBg(); return bg ? bg.jobs[key] : undefined; };

// Le finestre riportano il proprio stato (solo dati serializzabili: niente JSX/funzioni).
// IMPORTANTE: `bg` cambia a ogni modifica dei job -> NON deve stare nelle deps, altrimenti l'effetto
// ri-scrive il job in loop (e la × non riesce mai a rimuoverlo). Uso un ref per la funzione report.
export function useBgReport(key, meta, state) {
  const bg = useBg();
  const reportRef = useRef(null);
  reportRef.current = bg ? bg.report : null;
  const { active, running, done, error, summary, jobId } = state;
  useEffect(() => {
    const report = reportRef.current;
    if (!report || !active) return; // inattiva: NON rimuove (l'archivio persiste); si toglie solo con ×
    report(key, {
      label: meta.label, color: meta.color, iconKey: meta.iconKey,
      status: running ? 'running' : error ? 'error' : done ? 'done' : 'running',
      summary: summary || null, jobId: jobId || null,
    });
  }, [key, active, running, done, error, summary, jobId, meta.label, meta.color, meta.iconKey]); // eslint-disable-line
}

function Fab({ jkey, job, onOpen, onRemove }) {
  const running = job.status === 'running';
  return (
    <div className="bg-fab-wrap">
      <button type="button" onClick={() => onOpen(jkey)} className={running ? 'bg-fab bg-run' : 'bg-fab'}
        title={running ? `${job.label} in corso — apri` : `${job.label} — apri il risultato`} style={{ color: job.color }}>
        {ICONS[job.iconKey] || AiGlyph}
        {!running && <span className="bg-fab-dot" style={{ background: job.status === 'error' ? '#ef4444' : job.status === 'interrotto' ? '#94a3b8' : '#22c55e' }} />}
      </button>
      <button type="button" className="bg-fab-x" title="Chiudi" onClick={() => onRemove(jkey)}>×</button>
    </div>
  );
}

function FabStack({ jobs, openKey, onOpen, remove }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!panelOpen) return;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [panelOpen]);
  const visible = Object.entries(jobs).filter(([k]) => k !== openKey);
  if (!visible.length) return null;
  const anyRunning = visible.some(([, j]) => j.status === 'running');
  const single = visible.length === 1 ? visible[0] : null;

  return createPortal(
    <div className="bg-fab-stack">
      {single ? (
        <Fab jkey={single[0]} job={single[1]} onOpen={onOpen} onRemove={remove} />
      ) : (
        <div className="bg-fab-wrap" ref={panelRef}>
          <button type="button" className={anyRunning ? 'bg-fab bg-run' : 'bg-fab'} style={{ color: '#818cf8' }}
            title="Operazioni AI — apri l'elenco" onClick={() => setPanelOpen((o) => !o)}>
            {AiGlyph}
            <span className="bg-fab-count">{visible.length}</span>
          </button>
          {panelOpen && (
            <>
              <div className="bg-fab-panel">
                <div className="bg-fab-panel-head">Operazioni AI</div>
                {visible.map(([k, j]) => (
                  <div key={k} className="bg-fab-row">
                    <button type="button" className="bg-fab-row-main" onClick={() => { onOpen(k); setPanelOpen(false); }}>
                      <span className="bg-fab-row-ico" style={{ color: j.color }}>{ICONS[j.iconKey] || AiGlyph}</span>
                      <span className="bg-fab-row-lbl">{j.label}{j.summary && j.status !== 'running' ? ` · ${j.summary}` : ''}</span>
                      <span className="bg-fab-row-st" style={{ color: j.status === 'running' ? '#fbbf24' : j.status === 'error' ? '#fca5a5' : j.status === 'interrotto' ? '#94a3b8' : '#86efac' }}>
                        {j.status === 'running' ? 'in corso…' : j.status === 'error' ? 'errore' : j.status === 'interrotto' ? 'interrotto' : 'pronto'}
                      </span>
                    </button>
                    <button type="button" className="bg-fab-x2" title="Chiudi" onClick={() => remove(k)}>×</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}

export function BackgroundProvider({ onOpen, openKey, children }) {
  const [jobs, setJobs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; } catch { return {}; }
  });
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const resuming = useRef({});

  const api = useMemo(() => ({
    jobs,
    report: (key, patch) => setJobs((j) => ({ ...j, [key]: { ...(j[key] || {}), ...patch } })),
    remove: (key) => setJobs((j) => { if (!(key in j)) return j; const n = { ...j }; delete n[key]; return n; }),
  }), [jobs]);

  // persisti a ogni cambiamento
  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(jobs)); } catch { /* quota */ } }, [jobs]);

  // al caricamento: riprendi i job "running" (o marcali interrotti se non ripristinabili)
  useEffect(() => {
    const j0 = jobsRef.current;
    Object.entries(j0).forEach(([key, job]) => {
      if (job.status !== 'running') return;
      const fn = RESUMERS[key];
      if (!fn || !job.jobId) { api.report(key, { status: 'interrotto', summary: 'ripresa non disponibile' }); return; }
      if (resuming.current[key]) return;
      const rjid = job.jobId;
      resuming.current[key] = setInterval(async () => {
        // se l'utente ha lanciato un nuovo job per la stessa chiave (o l'ha chiuso), smetti
        const cur = jobsRef.current[key];
        if (!cur || cur.jobId !== rjid || cur.status !== 'running') { clearInterval(resuming.current[key]); delete resuming.current[key]; return; }
        try {
          const r = await fn(rjid);
          if (r) { clearInterval(resuming.current[key]); delete resuming.current[key]; api.report(key, { status: r.error ? 'error' : 'done', summary: r.summary || null }); }
        } catch { /* ritenta */ }
      }, 2500);
    });
    return () => { Object.values(resuming.current).forEach(clearInterval); resuming.current = {}; };
  }, []); // eslint-disable-line

  return (
    <BgCtx.Provider value={api}>
      {children}
      <FabStack jobs={jobs} openKey={openKey} onOpen={onOpen} remove={api.remove} />
    </BgCtx.Provider>
  );
}
